import { useCallback } from 'react'
import {
  useTrackProduct,
  trackProductViewed,
  useProductFiltersParams,
  trackProductClicked,
  type ProductEvent,
} from '@/features/shared/analytics/products'
import {
  type OnQtyCallback,
  type OnProductCallback,
} from '@/features/shop/components/ProductCard/types'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { type SearchCategory } from '@/features/shop/services/Search/types'

type Props = {
  currentCategory?: SearchCategory
  displaySublocation?: string
}

export const useOnProductGrid = ({ currentCategory, displaySublocation }: Props = {}) => {
  const filterParams = useProductFiltersParams(currentCategory)
  const { trackProductAdded, trackProductRemoved } = useTrackProduct()

  const getEventProperties = useCallback((): Omit<ProductEvent, 'product'> => {
    return {
      filterParams,
      displayFormat: DISPLAY_FORMAT.GRID,
      subLocation: displaySublocation,
    }
  }, [filterParams, displaySublocation])

  const onQtyAdded = useCallback<OnQtyCallback>(
    ({ product, index }) => {
      trackProductAdded({ product, gridIndex: index, ...getEventProperties() })
    },
    [getEventProperties, trackProductAdded],
  )

  const onQtyRemoved = useCallback<OnProductCallback>(
    ({ product, index }) => {
      trackProductRemoved({
        product,
        gridIndex: index,
        ...getEventProperties(),
      })
    },
    [getEventProperties, trackProductRemoved],
  )

  const onProductClick = useCallback<OnProductCallback>(
    ({ product, index }) => {
      trackProductClicked({
        product,
        gridIndex: index,
        ...getEventProperties(),
      })
    },
    [getEventProperties],
  )

  const onProductInView = useCallback<OnProductCallback>(
    ({ product, index }) => {
      trackProductViewed({
        product,
        gridIndex: index,
        ...getEventProperties(),
      })
    },
    [getEventProperties],
  )

  return {
    onQtyAdded,
    onQtyRemoved,
    onProductClick,
    onProductInView,
  }
}
